import { publicIdSchema } from "@rosc/contracts";
import { Loader2, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { V3Button } from "@/components/ui/v3/button";
import { env } from "@/env";
import axiosClientV2 from "@/lib/axios/axiosClientV2";
import { getVisitScope, useVisitScope } from "@/lib/visit-queries";
import { useTranscriptV3 } from "./transcript-style";

export function OwnedAudioPlayer({ source }: { source: string | null }) {
  const { t } = useTranslation();
  const v3 = useTranscriptV3();
  const scope = useVisitScope();
  const active = useRef({ scope, source });
  if (active.current.scope !== scope || active.current.source !== source)
    active.current = { scope, source };
  const lifetime = active.current;
  const controller = useRef<AbortController | undefined>(undefined);
  const player = useRef<HTMLAudioElement>(null);
  const [audio, setAudio] = useState<{
    scope: typeof scope;
    source: string;
    url: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    const element = player.current;
    return () => {
      element?.pause();
      if (audio) URL.revokeObjectURL(audio.url);
    };
  }, [audio]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Changing account or source must stop playback and clear the previous audio.
  useEffect(() => {
    setAudio(null);
    setError(false);
    setLoading(false);
    return () => controller.current?.abort();
  }, [scope, source]);
  if (!source || !scope) return null;
  const load = async () => {
    const operation = new AbortController();
    controller.current = operation;
    const current = () =>
      !operation.signal.aborted &&
      active.current === lifetime &&
      getVisitScope() === scope;
    setLoading(true);
    setError(false);
    try {
      const url = new URL(source, env.VITE_API_V2_BASE_URL);
      const path =
        /^\/api\/v2\/visits\/([^/]+)\/(?:recordings\/([^/]+)\/)?audio\/content$/.exec(
          url.pathname,
        );
      if (
        url.origin !== new URL(env.VITE_API_V2_BASE_URL).origin ||
        !path ||
        !publicIdSchema.safeParse(path[1]).success ||
        (path[2] !== undefined && !publicIdSchema.safeParse(path[2]).success) ||
        url.search ||
        url.hash
      )
        throw Error("Invalid media source");
      const response = await axiosClientV2.get<Blob>(url.href, {
        responseType: "blob",
        expectedUserUid: scope.uid,
        signal: operation.signal,
      });
      if (!current()) return;
      setAudio({ scope, source, url: URL.createObjectURL(response.data) });
    } catch {
      if (current()) setError(true);
    } finally {
      if (current()) setLoading(false);
    }
  };
  return (
    <div className="my-3">
      {audio?.scope === scope && audio.source === source ? (
        // biome-ignore lint/a11y/useMediaCaption: The recording transcript appears directly beside this player.
        <audio
          ref={player}
          aria-label={t("visit.audioPlayer.playback")}
          controls
          src={audio.url}
        />
      ) : v3 ? (
        <V3Button
          data-testid="listen-recording"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? (
            <Loader2 aria-hidden className="animate-spin" />
          ) : (
            <Play aria-hidden className="size-3.5!" fill="currentColor" />
          )}
          {loading
            ? t("visit.audioPlayer.loading")
            : t("visit.audioPlayer.listen")}
        </V3Button>
      ) : (
        <Button
          disabled={loading}
          size="sm"
          variant="outline"
          onClick={() => void load()}
        >
          {loading
            ? t("visit.audioPlayer.loading")
            : t("visit.audioPlayer.listen")}
        </Button>
      )}
      {error && (
        <p
          className={v3 ? "mt-1 text-[13px] text-v3-rec-text" : undefined}
          role="alert"
        >
          {t("visit.audioPlayer.loadFailed")}
        </p>
      )}
    </div>
  );
}
